// Update to packages/web/src/components/user/UserSettings.tsx
import React, { useState, useEffect } from 'react';
import { Save, RotateCcw, X } from 'lucide-react';
import { api } from '../../services/api';
import { User } from '../../types';
import EmailAuthorization from '../settings/EmailAuthorization';
import CalendarAuthorization from '../settings/CalendarAuthorization';
import TelegramConnection from '../settings/TelegramConnection';
import ConnectedAccounts from '../settings/ConnectedAccounts';

interface UserSettingsProps {
  onClose: () => void;
}

// Define the settings interface
interface Settings {
  coldThresholdDays: number;
  notifications: {
    birthdays: boolean;
    dueTodos: boolean;
  };
}

const DEFAULT_SETTINGS: Settings = {
  coldThresholdDays: 30, // default to 30 days
  notifications: {
    birthdays: true,
    dueTodos: true
  }
};

const UserSettings: React.FC<UserSettingsProps> = ({ onClose }) => {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const userSettings = await api.getUserSettings();
      setSettings(userSettings || DEFAULT_SETTINGS);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
      setSettings(DEFAULT_SETTINGS);
      setSaveMessage({ type: 'error', text: 'Could not load settings.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await api.updateUserSettings(settings);
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all settings to their defaults?')) {
      setIsSaving(true);
      setSaveMessage(null);
      try {
        setSettings(DEFAULT_SETTINGS);
        await api.updateUserSettings(DEFAULT_SETTINGS);
        setSaveMessage({ type: 'success', text: 'Settings reset to default.' });
        setTimeout(() => setSaveMessage(null), 3000);
      } catch (error) {
        console.error('Failed to reset settings:', error);
        setSaveMessage({ type: 'error', text: 'Failed to reset settings.' });
        fetchSettings();
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div 
       className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-out"
       onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Settings</h2>
          <button 
            onClick={onClose} 
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            disabled={isSaving}
            aria-label="Close settings"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 overflow-y-auto flex-grow space-y-6">
          {isLoading && (
             <div className="text-center py-10 text-gray-500">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mx-auto mb-3"></div>
               Loading settings...
             </div>
           )}
           
          {!isLoading && (
             <>
               {saveMessage && (
                 <div className={`p-3 rounded-lg border text-sm mb-4 ${saveMessage.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                   {saveMessage.text}
                 </div>
               )}

               <div className="bg-white border border-gray-200 rounded-lg p-5">
                 <h3 className="text-base font-semibold mb-4 text-gray-800">Contact Status</h3>
                 <div>
                   <label htmlFor="coldThreshold" className="block text-sm font-medium text-gray-700 mb-1">
                     Mark contacts as cold after (days without interaction)
                   </label>
                   <div className="flex items-center">
                     <input
                       type="number"
                       id="coldThreshold"
                       min="1"
                       max="365"
                       value={settings.coldThresholdDays}
                       onChange={(e) => setSettings({
                         ...settings,
                         coldThresholdDays: parseInt(e.target.value) || DEFAULT_SETTINGS.coldThresholdDays
                       })}
                       className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-1 focus:ring-brand-primary focus:border-brand-primary bg-gray-50 text-sm"
                       disabled={isSaving}
                     />
                     <span className="ml-2 text-sm text-gray-600">days</span>
                   </div>
                   <p className="mt-1 text-xs text-gray-500">
                     Contacts are automatically marked "cold" after this period of inactivity.
                   </p>
                 </div>
               </div>

               <div className="bg-white border border-gray-200 rounded-lg p-5">
                 <h3 className="text-base font-semibold mb-4 text-gray-800">Notifications</h3>
                 <div className="space-y-3">
                   <div className="flex items-start">
                     <input
                       type="checkbox"
                       id="birthday-notifications"
                       checked={settings.notifications.birthdays}
                       onChange={(e) => setSettings({
                         ...settings,
                         notifications: { ...settings.notifications, birthdays: e.target.checked }
                       })}
                       className="mt-0.5 h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                       disabled={isSaving}
                     />
                     <label htmlFor="birthday-notifications" className="ml-2 block text-sm text-gray-700">
                       Birthday reminders
                     </label>
                   </div>
                   <div className="flex items-start">
                     <input
                       type="checkbox"
                       id="todo-notifications"
                       checked={settings.notifications.dueTodos}
                       onChange={(e) => setSettings({
                         ...settings,
                         notifications: { ...settings.notifications, dueTodos: e.target.checked }
                       })}
                       className="mt-0.5 h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300 rounded"
                       disabled={isSaving}
                     />
                     <label htmlFor="todo-notifications" className="ml-2 block text-sm text-gray-700">
                       Due TODO reminders
                     </label>
                   </div>
                 </div>
               </div>

               <ConnectedAccounts />
               <EmailAuthorization />
               <CalendarAuthorization />
               <TelegramConnection />
             </>
           )}
        </div>

        {!isLoading && (
          <div className="flex justify-between items-center gap-3 p-4 bg-gray-50 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-1.5 bg-white text-gray-600 px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-100 hover:text-red-600 hover:border-red-300 transition-colors text-sm font-medium disabled:opacity-50"
              disabled={isSaving}
            >
              <RotateCcw size={16} /> Reset Defaults
            </button>
            <button
              type="button"
              onClick={handleSave}
              className={`flex items-center gap-1.5 bg-brand-primary text-white px-4 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 ${
                isSaving ? 'cursor-not-allowed' : ''
              }`}
              disabled={isSaving}
            >
              <Save size={16} /> {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettings;